import { useRef } from 'react';
import { noop } from 'lodash';
import { ChronoUnit, LocalDate, LocalTime } from '@js-joda/core';
import { useDeepCompareEffect, useFirstMountState } from 'react-use';
import { useWatch } from 'react-hook-form';

import { AppointmentNewSlotAvailabilityQuery } from '@dreryk/egabinet-appointment-frontend-api';
import { AppointmentId } from '@dreryk/egabinet-appointment-published-language-api';

import { performCqrsQueryGet } from '@dreryk/command-query-react-common';
import { Range } from '@dreryk/watermelon';

import { parseToDateWithMidnightAdjustment } from '../AppointmentForm.utils';
import { AppointmentFormController } from '../AppointmentForm.types';
import receptionService from '../../../../../receptionService';
import { ReceptionServiceTableBean } from '../../../../../receptionService/extensions/calendar/appointment/interactiveTable/ReceptionServiceInteractiveTable.types';

type AppointmentNewSlotAvailabilityQueryReturnType = (
   date: LocalDate,
   startTime: LocalTime,
   endTime: LocalTime
) => Promise<boolean>;

export const useAppointmentNewSlotAvailabilityChecker = (
   appointmentId?: number
): AppointmentNewSlotAvailabilityQueryReturnType => {
   return async (date: LocalDate, startTime: LocalTime, endTime: LocalTime): Promise<boolean> => {
      if (!appointmentId) {
         return true;
      }

      const result = await performCqrsQueryGet(() => {
         const query = new AppointmentNewSlotAvailabilityQuery();
         query.appointmentId = AppointmentId.of(appointmentId);
         query.slotDateRange = Range.closed(
            parseToDateWithMidnightAdjustment('START', date, startTime),
            parseToDateWithMidnightAdjustment('END', date, endTime)
         );
         return query;
      });

      return Boolean(result);
   };
};

export const useSyncTimeWithEstimatedDuration = (
   control: AppointmentFormController['control'],
   formState: AppointmentFormController['formState'],
   setValue: AppointmentFormController['setValue'],
   watch: AppointmentFormController['watch']
): void => {
   const firstMount = useFirstMountState();
   const formValues = watch();
   const receptionServiceValue = useWatch<ReceptionServiceTableBean[]>({
      name: 'receptionService.value',
      control: control,
      defaultValue: formValues.receptionService?.value ?? [],
   });

   const startDateTime = formValues.date?.atTime(formValues.startTime);
   const endDateTime = formValues.date?.atTime(formValues.endTime);

   const appointmentEndDateTimeValueRef = useRef(
      endDateTime?.toLocalTime().equals(LocalTime.MIDNIGHT) ? formValues.date?.plusDays(1).atStartOfDay() : endDateTime
   );
   const appointmentStartDateTimeValueRef = useRef(startDateTime);
   const calculatedTotalEstimatedDuration = receptionService.getCalculatedTotalEstimatedDuration(receptionServiceValue);
   const initialTotalEstimatedDuration =
      appointmentStartDateTimeValueRef.current && appointmentEndDateTimeValueRef.current
         ? appointmentStartDateTimeValueRef.current.until(appointmentEndDateTimeValueRef.current, ChronoUnit.MINUTES)
         : 0;
   const remainingMinutesInDay = formValues.startTime
      ? formValues.startTime.until(LocalTime.MAX, ChronoUnit.MINUTES)
      : 0;

   useDeepCompareEffect(() => {
      if (formState.touched.endTime || formState.touched.estimatedDuration || firstMount || !formValues.startTime) {
         return noop();
      }

      if (calculatedTotalEstimatedDuration >= remainingMinutesInDay) {
         return setValue('endTime', LocalTime.MIDNIGHT);
      }

      if (calculatedTotalEstimatedDuration) {
         setValue('estimatedDuration', calculatedTotalEstimatedDuration);
         return setValue('endTime', formValues.startTime.plusMinutes(calculatedTotalEstimatedDuration));
      }

      return noop();
   }, [calculatedTotalEstimatedDuration, formValues.startTime]);

   useDeepCompareEffect(() => {
      if (
         formState.touched.endTime ||
         formState.touched.estimatedDuration ||
         !formState.isDirty ||
         firstMount ||
         !formValues.startTime
      ) {
         return noop();
      }

      if (receptionServiceValue.length === 0) {
         setValue('estimatedDuration', initialTotalEstimatedDuration);
         return setValue('endTime', formValues.startTime.plusMinutes(initialTotalEstimatedDuration));
      }

      return setValue('estimatedDuration', calculatedTotalEstimatedDuration);
   }, [receptionServiceValue]);
};
